import {Directive, ElementRef, HostBinding, Input, NgZone, OnDestroy, OnInit, Renderer2} from "@angular/core";
import {AgGridAngular} from "ag-grid-angular";
import {fromEvent, Observable, Subscription} from "rxjs";
import {debounceTime} from "rxjs/operators";

@Directive({
  standalone: false,
  selector: "[full-width-table]"
})
export class FullWidthTableDirective implements OnInit, OnDestroy {

  @Input()
  tableRef: AgGridAngular;

  private resizeStream$: Observable<any>;
  private resizeSubscription: Subscription;
  // ESP a11y (WCAG 2.2 SC 4.1.2 Name, Role, Value):
  // AG Grid v31 renders multiple role="rowgroup" containers (pinned-left/right
  // headers, pinned-left/right body, ag-full-width-container, sticky top/bottom
  // containers and their full-width variants, center-cols-container) regardless
  // of whether they currently contain rows. axe/ARC flag any empty rowgroup
  // as "Parent role 'rowgroup' is missing the row element". A MutationObserver
  // on the grid host swaps role="rowgroup" to role="presentation" while the
  // container is empty, and restores it once a role="row" descendant appears.
  private rowgroupObserver: MutationObserver | null = null;
  private rowgroupRafHandle: number | null = null;

  constructor(private renderer: Renderer2,
              private host: ElementRef<HTMLElement>,
              private ngZone: NgZone) {
  }

  @HostBinding("class.full-width-table")
  fullWidthTable = true;

  private resizeTable() {
    this.tableRef.api.sizeColumnsToFit();
  }

  ngOnInit() {
    this.resizeStream$ = fromEvent(window, "resize").pipe(debounceTime(500));
    this.resizeSubscription = this.resizeStream$.subscribe(this.resizeTable.bind(this));
    this.installRowgroupRolePatch();
  }

  ngOnDestroy() {
    this.resizeSubscription.unsubscribe();
    if (this.rowgroupObserver) {
      this.rowgroupObserver.disconnect();
      this.rowgroupObserver = null;
    }
    if (this.rowgroupRafHandle !== null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(this.rowgroupRafHandle);
      this.rowgroupRafHandle = null;
    }
  }

  private installRowgroupRolePatch(): void {
    if (typeof MutationObserver === "undefined") {
      return;
    }
    const root = this.host?.nativeElement;
    if (!root) {
      return;
    }
    // Run outside Angular to avoid triggering change detection on every DOM mutation.
    this.ngZone.runOutsideAngular(() => {
      const schedule = () => {
        if (this.rowgroupRafHandle !== null) {
          return;
        }
        const raf = typeof requestAnimationFrame === "function"
          ? requestAnimationFrame
          : (cb: FrameRequestCallback) => window.setTimeout(() => cb(0), 16) as unknown as number;
        this.rowgroupRafHandle = raf(() => {
          this.rowgroupRafHandle = null;
          this.patchEmptyRowgroups(root);
        });
      };
      this.rowgroupObserver = new MutationObserver(schedule);
      this.rowgroupObserver.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["role"]
      });
      // Initial pass once the grid markup has rendered.
      schedule();
    });
  }

  private patchEmptyRowgroups(root: HTMLElement): void {
    const candidates = root.querySelectorAll<HTMLElement>(
      '[role="rowgroup"], [data-empty-rowgroup="true"]'
    );
    candidates.forEach((el) => {
      const hasRow = el.querySelector('[role="row"]') !== null;
      if (hasRow) {
        if (el.getAttribute("data-empty-rowgroup") === "true") {
          el.setAttribute("role", "rowgroup");
          el.removeAttribute("data-empty-rowgroup");
          // Restore the scroll-region tab stop that was removed while empty.
          const savedTabindex = el.getAttribute("data-empty-rowgroup-tabindex");
          if (savedTabindex !== null) {
            el.setAttribute("tabindex", savedTabindex);
            el.removeAttribute("data-empty-rowgroup-tabindex");
          }
        }
      } else if (el.getAttribute("role") === "rowgroup") {
        el.setAttribute("role", "presentation");
        el.setAttribute("data-empty-rowgroup", "true");
        // A focusable element cannot carry role="presentation": browsers resolve
        // the conflict by IGNORING the presentation role and re-expose the
        // element, where it becomes an unallowed child of role="grid"
        // (axe aria-required-children). makeGridScrollRegionFocusable() may have
        // put a tabindex on this rowgroup for scroll access, but an empty
        // rowgroup has nothing to scroll, so drop the tab stop while empty and
        // restore it (above) once rows return.
        if (el.hasAttribute("tabindex")) {
          el.setAttribute("data-empty-rowgroup-tabindex", el.getAttribute("tabindex") as string);
          el.removeAttribute("tabindex");
        }
      }
    });
    this.patchNoRowsOverlay(root);
  }

  // ESP a11y (WCAG 1.3.1 / axe aria-required-children):
  // AG Grid renders its "no rows" overlay (e.g. the "No plans defined yet"
  // message) INSIDE the role="grid" element. The overlay's heading/graphics are
  // not allowed direct children of a grid, so axe flags the grid. Wrap the
  // overlay in a valid grid > row > gridcell chain so the message keeps being
  // exposed to screen readers while satisfying the required-children contract.
  private patchNoRowsOverlay(root: HTMLElement): void {
    const wrapper = root.querySelector<HTMLElement>(".ag-overlay-no-rows-wrapper");
    if (!wrapper) {
      return;
    }
    if (wrapper.getAttribute("role") !== "row") {
      wrapper.setAttribute("role", "row");
    }
    const cell = wrapper.firstElementChild as HTMLElement | null;
    if (cell && cell.getAttribute("role") !== "gridcell") {
      cell.setAttribute("role", "gridcell");
    }
  }
}
